/**
 * ThemePicker — a chip-style radio group that switches the active theme.
 *
 * Each chip is rendered with the theme's primary gradient color so the
 * picker doubles as a preview swatch. Selecting a chip calls
 * `useThemeStore.apply(id)`, which lazy-loads the new theme's assets,
 * applies CSS custom properties, and persists the choice (Requirement 8.7) —
 * the theme stage cross-fades between backgrounds within the manifest's
 * `crossfadeMs` budget (Requirement 8.4). Reduced-motion users get a 150 ms
 * fade via the existing reduced-motion derivation (Requirement 7.5).
 *
 * Validates: Requirements 7.5, 8.4, 8.7.
 */

import type { ReactElement } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { LanguageCode } from '../../domain/types';
import { useThemeStore } from '../../state';

import styles from './ThemePicker.module.css';

export function ThemePicker(): ReactElement {
  const { i18n, t } = useTranslation();
  const current = useThemeStore((s) => s.current);
  const apply = useThemeStore((s) => s.apply);
  const list = useThemeStore((s) => s.list)();

  // The i18n language always belongs to the LanguageCode union by the time
  // the chat surface mounts, but a quick narrowing keeps TypeScript happy
  // and falls back to English if a downstream consumer ever ships an
  // unknown language tag.
  const lang: LanguageCode =
    i18n.language === 'tr' || i18n.language === 'el' || i18n.language === 'en'
      ? (i18n.language as LanguageCode)
      : 'en';

  const handleApply = (id: string): void => {
    if (current.id === id) return;
    void apply(id);
  };

  return (
    <div
      className={styles.picker}
      role="radiogroup"
      aria-label={t('a11y.themePicker')}
      data-testid="theme-picker"
    >
      {list.map((manifest) => {
        const isActive = current.id === manifest.id;
        const label = manifest.displayName[lang] ?? manifest.displayName.en;
        const [gradStart, gradEnd] = manifest.colors.bgGradient;
        return (
          <motion.button
            key={manifest.id}
            type="button"
            onClick={() => handleApply(manifest.id)}
            role="radio"
            aria-checked={isActive}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            transition={{ duration: 0.12, ease: [0.2, 0.8, 0.2, 1] }}
            className={[styles.chip, isActive ? styles.chipActive : '']
              .filter(Boolean)
              .join(' ')}
            style={{
              background: `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`,
              color: manifest.colors.textPrimary,
              borderColor: isActive ? manifest.colors.focusRing : 'transparent',
            }}
            data-theme-id={manifest.id}
          >
            {label}
          </motion.button>
        );
      })}
    </div>
  );
}
